import "./style.css"

import { CodeHighlightNode, CodeNode } from "@lexical/code"
import { AutoLinkNode, LinkNode } from "@lexical/link"
import { ListItemNode, ListNode } from "@lexical/list"
import { TRANSFORMERS } from "@lexical/markdown"
import { LexicalComposer } from "@lexical/react/LexicalComposer"
import { ContentEditable } from "@lexical/react/LexicalContentEditable"
import LexicalErrorBoundary from "@lexical/react/LexicalErrorBoundary"
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin"
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin"
import { ListPlugin } from "@lexical/react/LexicalListPlugin"
import { MarkdownShortcutPlugin } from "@lexical/react/LexicalMarkdownShortcutPlugin"
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin"
import { HeadingNode, QuoteNode } from "@lexical/rich-text"
import { TableCellNode, TableNode, TableRowNode } from "@lexical/table"
import React from "react"
import { useTranslation } from "react-i18next"

import AutoLinkPlugin from "./plugins/AutoLinkPlugin"
import CodeHighlightPlugin from "./plugins/CodeHighlightPlugin"
import ListMaxIndentLevelPlugin from "./plugins/ListMaxIndentLevelPlugin"
import { MaxLengthPlugin } from "./plugins/MaxLengthPlugin"
import OnChangePlugin from "./plugins/OnChangePlugin"
import ToolbarPlugin from "./plugins/ToolbarPlugin"
import RichTextTheme from "./themes/RichTextTheme"

const emptyContent = "{\"root\":{\"children\":[{\"children\":[],\"direction\":null,\"format\":\"\",\"indent\":0,\"type\":\"paragraph\",\"version\":1,\"textStyle\":\"\"}],\"direction\":null,\"format\":\"\",\"indent\":0,\"type\":\"root\",\"version\":1}}"
const RichTextEditor = ({
    id,
    label,
    name,
    richTextData,
    errors,
    isRequired,
    dataTestId,
    className,
    onChangeRichTextData,
    customErrorObjName,
    isComponentLabelValidation = true,
    dataId,
    isDefault,
    disabled,
    readOnly = false,
    maxLength,
    handleErrorForRichTextBox
}) => {
    const editorConfig = {
        // The editor theme
        theme: RichTextTheme,
        editable: readOnly ? !readOnly : !disabled,
        editorState: richTextData || emptyContent,
        // Handling of errors during update
        onError(error) {
            throw error
        },
        // Any custom nodes go here
        nodes: [
            HeadingNode,
            ListNode,
            ListItemNode,
            QuoteNode,
            CodeNode,
            CodeHighlightNode,
            TableNode,
            TableCellNode,
            TableRowNode,
            AutoLinkNode,
            LinkNode
        ]
    }
    const onChange = (currentState) => {
        // Call toJSON on the EditorState object, which produces a serialization safe string
        const editorStateJSON = currentState.toJSON()
        const editorStateStringfyJSON = JSON.stringify(editorStateJSON)
        const addedText = editorStateJSON?.root?.children?.[0]?.children?.[0]?.text
        const textformat = editorStateJSON?.root?.children?.[0]?.textFormat
        // However, we still have a JavaScript object, so we need to convert it to an actual string with JSON.stringify
        if (handleErrorForRichTextBox) {
            if (addedText) {
                onChangeRichTextData(dataId, isDefault, name, editorStateStringfyJSON)
            } else if (!addedText && textformat != undefined) {
                onChangeRichTextData(dataId, isDefault, name, "")
            }
        } else {
            onChangeRichTextData(dataId, isDefault, name, editorStateStringfyJSON)
        }
        
    }
    const isValid = !(isRequired && errors && name && errors[name])
    const CLASS_NAME =
      className ||
      "w-full p-3 text-sm text-gray-900 rounded-lg"+
      " border border-lightGray appearance-none focus:outline-none"+ 
      " focus:ring-0 focus:border-primary-100 peer"
    const INPUT_CLASS = isValid
        ? `block disabled:bg-lighestgray h-[auto] focus:outline-none 
      focus:ring-0 focus:border-primary-100 focus:bg-lightSkyblue 
      hover:border-primary-100 transition duration-300 ${CLASS_NAME}`
        : `block disabled:bg-lighestgray border-red h-[auto] 
      focus:outline-none focus:ring-0 focus:border-primary-100 
      focus:bg-lightSkyblue hover:border-primary-100 transition duration-300 ${CLASS_NAME}`
    const { t } = useTranslation()
    return (
        <LexicalComposer initialConfig={ editorConfig }>
            <div className={ readOnly ? "relative editor-container" : `${INPUT_CLASS} relative editor-container` }>
                { !readOnly && (<ToolbarPlugin 
                    disabled={ disabled }
                    readOnly={ readOnly }
                />) }
                <div className="editor-inner">
                    <RichTextPlugin
                        contentEditable={ <ContentEditable className={ readOnly ? "editor-input-readonly" : 
                            (disabled ? "editor-input-disabled" : "editor-input") } /> }
                        ErrorBoundary={ LexicalErrorBoundary }
                        id={ id }
                        data-testid={ dataTestId }
                        placeholder=""
                        name={ name }
                    />
                    <MaxLengthPlugin maxLength={ maxLength || 250 } />
                    <HistoryPlugin />
                    { /* <TreeViewPlugin /> */ }
                    { /* <AutoFocusPlugin /> */ }
                    <CodeHighlightPlugin />
                    <ListPlugin />
                    <LinkPlugin />
                    <AutoLinkPlugin />
                    <ListMaxIndentLevelPlugin maxDepth={ 7 } />
                    <MarkdownShortcutPlugin transformers={ TRANSFORMERS } />
                    <OnChangePlugin onChange={ onChange }/>
                </div>
                <label
                    htmlFor={ id }
                    className="absolute flex text-sm text-secondary-50 duration-300 
                    transform -translate-y-[28px] scale-75 top-16 origin-[0] bg-white 
                    dark:bg-gray-900 px-2 peer-focus:px-2 peer-focus:text-blue-600 
                    peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 
                    peer-placeholder-shown:top-1/2 peer-focus:top-16 peer-focus:scale-75 
                    peer-focus:-translate-y-[28px] rtl:peer-focus:translate-x-1/2 
                    rtl:peer-focus:left-auto start-17 cursor-text font-['Avenir'] font-normal"
                >
                    { isRequired && <span className="text-red text-sm mr-1 flex">*</span> }
                    { label || name }
                </label>
            </div>
            { isComponentLabelValidation && errors && name && errors[name] && (
                <span className="text-red text-xs font-normal font-['Avenir'] leading-[14px]">
                    { customErrorObjName ? t(`errorMessage.${customErrorObjName}.${name}`) : t(`errorMessage.${name}`) }
                </span>
            ) }
        </LexicalComposer>
    )
}

export default RichTextEditor
